import { useEffect, useState, type FormEvent } from "react";

import { submitArtistOnboarding } from "@/api/onboarding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import GenrePicker from "@/components/onboarding/GenrePicker";
import type { OnboardingStatus } from "@/types/onboarding";

/** Entered values kept by the flow while moving between steps. */
export type ArtistDraft = {
  name: string;
  city: string;
  region: string;
  /** Comma-separated lowercase genres (draft format kept stable across steps). */
  genres: string;
  website: string;
  bandsintown: string;
  spotify: string;
};

type Props = {
  draft?: ArtistDraft | null;
  /** Saved artist (from onboarding status) used when there is no draft. */
  savedArtist?: { name: string; genres: string[] } | null;
  onDraftChange?: (draft: ArtistDraft) => void;
  onBack?: () => void;
  onDone: (status: OnboardingStatus) => Promise<void> | void;
};

/** Step 2: create the managed artist. Only the name is required. */
export default function ArtistBasicsStep({ draft, savedArtist, onDraftChange, onBack, onDone }: Props) {
  const [name, setName] = useState(draft?.name ?? savedArtist?.name ?? "");
  const [city, setCity] = useState(draft?.city ?? "");
  const [region, setRegion] = useState(draft?.region ?? "");
  const [genres, setGenres] = useState(draft?.genres ?? (savedArtist?.genres ?? []).join(", "));
  const genreList = genres.split(",").map((genre) => genre.trim()).filter(Boolean);
  const [website, setWebsite] = useState(draft?.website ?? "");
  const [bandsintown, setBandsintown] = useState(draft?.bandsintown ?? "");
  const [spotify, setSpotify] = useState(draft?.spotify ?? "");

  useEffect(() => {
    onDraftChange?.({ name, city, region, genres, website, bandsintown, spotify });
  }, [name, city, region, genres, website, bandsintown, spotify, onDraftChange]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const optional = (value: string) => (value.trim() ? value.trim() : undefined);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const status = await submitArtistOnboarding({
        name: name.trim(),
        home_city: optional(city),
        home_region: optional(region),
        genres: genreList,
        website: optional(website),
        bandsintown_url: optional(bandsintown),
        spotify_url: optional(spotify),
      });
      await onDone(status);
    } catch {
      setError("We couldn't create your artist. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit} aria-label="Artist">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Your artist</h2>
        <p className="text-sm text-muted-foreground">Just the name is required. You can fill in the rest any time.</p>
      </div>
      <label className="block text-sm text-muted-foreground">
        Artist or band name
        <Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)} aria-label="Artist name" />
      </label>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-muted-foreground">
          Home city
          <Input className="mt-1" value={city} onChange={(e) => setCity(e.target.value)} aria-label="Home city" placeholder="e.g. Boise" />
        </label>
        <label className="text-sm text-muted-foreground">
          State / region
          <Input className="mt-1" value={region} onChange={(e) => setRegion(e.target.value)} aria-label="Home region" placeholder="e.g. ID" />
        </label>
      </div>
      <div className="text-sm text-muted-foreground">
        <label htmlFor="artist-genres">Genres</label>
        <GenrePicker id="artist-genres" value={genreList} onChange={(next) => setGenres(next.join(", "))} />
      </div>
      <details className="rounded-lg border border-border/60 px-3 py-2 text-sm">
        <summary className="cursor-pointer text-muted-foreground">Add links (optional)</summary>
        <div className="mt-3 space-y-3">
          <label className="block text-sm text-muted-foreground">
            Website
            <Input className="mt-1" value={website} onChange={(e) => setWebsite(e.target.value)} aria-label="Website" />
          </label>
          <label className="block text-sm text-muted-foreground">
            Bandsintown page
            <Input className="mt-1" value={bandsintown} onChange={(e) => setBandsintown(e.target.value)} aria-label="Bandsintown page" />
          </label>
          <label className="block text-sm text-muted-foreground">
            Spotify link
            <Input className="mt-1" value={spotify} onChange={(e) => setSpotify(e.target.value)} aria-label="Spotify link" />
          </label>
        </div>
      </details>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <div className="flex items-center justify-between">
        {onBack ? (
          <Button type="button" variant="ghost" onClick={onBack} disabled={saving}>
            Back
          </Button>
        ) : (
          <span />
        )}
        <Button type="submit" disabled={!name.trim() || saving}>
          {saving ? "Saving…" : "Continue"}
        </Button>
      </div>
    </form>
  );
}
